/**
 * Meeting preference presets for FreeBusy / default slot suggestions.
 *
 * Structured hours only — soft prose like "I like mornings" belongs in About you.
 */

export const MEETING_PREF_PRESET_DEFAULT = "default";
export const MEETING_PREF_PRESET_MORNING = "morning";
export const MEETING_PREF_PRESET_AFTERNOON = "afternoon";
export const MEETING_PREF_PRESET_CUSTOM = "custom";

export const MEETING_PREF_PRESETS = [
  {
    id: MEETING_PREF_PRESET_DEFAULT,
    label: "Work hours",
    chipLabel: "Work hours · 9am–6pm",
    description: "Suggest within 9am–6pm",
    start: null,
    end: null,
  },
  {
    id: MEETING_PREF_PRESET_MORNING,
    label: "Mornings",
    chipLabel: "Mornings · 9am–12pm",
    description: "9am–12pm",
    start: 9,
    end: 12,
  },
  {
    id: MEETING_PREF_PRESET_AFTERNOON,
    label: "Afternoons",
    chipLabel: "Afternoons · 1pm–5pm",
    description: "1pm–5pm",
    start: 13,
    end: 17,
  },
  {
    id: MEETING_PREF_PRESET_CUSTOM,
    label: "Custom",
    chipLabel: "Custom",
    description: "Pick your hours",
    start: null,
    end: null,
  },
];

/**
 * Map saved hours to a preset id.
 *
 * @example
 * resolveMeetingPrefPreset(9, 12); // "morning"
 * resolveMeetingPrefPreset(null, null); // "default"
 * resolveMeetingPrefPreset(10, 14); // "custom"
 */
export const resolveMeetingPrefPreset = (start, end) => {
  if (start == null && end == null) {
    return MEETING_PREF_PRESET_DEFAULT;
  }
  const startH = Number(start);
  const endH = Number(end);
  const match = MEETING_PREF_PRESETS.find(
    (preset) =>
      preset.id !== MEETING_PREF_PRESET_CUSTOM &&
      preset.id !== MEETING_PREF_PRESET_DEFAULT &&
      preset.start === startH &&
      preset.end === endH
  );
  return match ? match.id : MEETING_PREF_PRESET_CUSTOM;
};

/**
 * Build a short hub summary for meeting preferences.
 *
 * @example
 * summarizeMeetingPreferences(9, 12); // "Mornings (9am–12pm)"
 */
export const summarizeMeetingPreferences = (start, end) => {
  const presetId = resolveMeetingPrefPreset(start, end);
  if (presetId === MEETING_PREF_PRESET_DEFAULT) {
    return "Uses 9am–6pm until you customize";
  }
  const preset = MEETING_PREF_PRESETS.find((item) => item.id === presetId);
  if (presetId !== MEETING_PREF_PRESET_CUSTOM && preset) {
    return `${preset.label} (${preset.description})`;
  }
  const formatHour = (hour) => {
    const h = Number(hour);
    if (Number.isNaN(h)) return "";
    const suffix = h >= 12 ? "pm" : "am";
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display}${suffix}`;
  };
  return `Custom (${formatHour(start)}–${formatHour(end)})`;
};

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  const suffix = hour >= 12 ? "pm" : "am";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return { value: hour, label: `${display}:00 ${suffix}` };
});

/** localStorage key: first-time Calendar connect nudge on default-slot drafts. */
export const CALENDAR_CONNECT_NUDGE_DISMISSED_KEY =
  "starmesh.calendarConnectNudgeDismissed";

/**
 * Whether to show the one-time Calendar connect nudge.
 *
 * @example
 * shouldShowCalendarConnectNudge({ slot_source: "defaults", calendar_connected: false });
 * // true when not dismissed
 */
export const shouldShowCalendarConnectNudge = (result) => {
  if (typeof window === "undefined") return false;
  if (window.localStorage.getItem(CALENDAR_CONNECT_NUDGE_DISMISSED_KEY) === "1") {
    return false;
  }
  const slotSource = String(result?.slot_source || "").trim().toLowerCase();
  if (slotSource !== "defaults") return false;
  if (result?.calendar_connected === true) return false;
  return true;
};

/**
 * Persist dismissal of the Calendar connect nudge.
 *
 * @example
 * dismissCalendarConnectNudge();
 */
export const dismissCalendarConnectNudge = () => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CALENDAR_CONNECT_NUDGE_DISMISSED_KEY, "1");
};
